import { useCallback } from 'react';
import { type Href, useLocalSearchParams } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@sg/garnish';

import { useGlobalAppState } from '../../../../context/GlobalAppState';
import {
  useLastInteractedStoreMachine,
  useSetLastInteractedMenuStore,
} from '../../../../context/LastInteractedStore';
import { useCart, useNavigateToMenu, type UseNavigateToMenuProps } from '../../../../hooks';
import { storeDismissedGroupOrderId } from '../../../../hooks/useGroupOrderingState/group-order-reference-storage';
import { useGroupOrderTelemetry } from '../../../../hooks/useGroupOrderTelemetry';
import {
  type ReorderLeaveGroupOrderMutation,
  useReorderLeaveGroupOrderMutation,
} from '../../GraphQL/ReorderGroupOrder.generated';

import type { ReorderEvent } from '../../machines/Reorder.machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Removes a participant from their group order, then opens the menu of the
 * location they picked while trying to move the group order.
 */
export const useLeaveGroupOrderAndContinue = (params: Params = {}) => {
  const { isInsideModal } = params;

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const { reorderMachineRef } = useGlobalAppState();
  const { cart } = useCart();
  const [{ fetching: isLeavingGroupOrder }, executeLeaveGroupOrder] =
    useReorderLeaveGroupOrderMutation();
  const { send: sendToLastInteractedStoreMachine } = useLastInteractedStoreMachine();
  const setLastInteractedMenuStore = useSetLastInteractedMenuStore();
  const { trackLeaveSuccess } = useGroupOrderTelemetry();
  const routeParams = useLocalSearchParams<{ backToPath?: string; category?: string }>();
  const navigateToMenu = useNavigateToMenu({
    backToPath: routeParams.backToPath as Href | undefined,
    isInsideModal,
  });

  // ─── Derived ───────────────────────────────────────────────────────────────

  const groupOrderId = cart?.id;
  const category = routeParams.category;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const leaveGroupOrder = useCallback(async () => {
    const response = await executeLeaveGroupOrder({});
    const hasLeftGroupOrder = checkIfLeftGroupOrder({
      result: response.data?.leaveMultipartyOrder,
    });

    if (!hasLeftGroupOrder) {
      addNoticeBanner({ text: formatMessage(messages.leaveError), palette: 'caution' });

      return false;
    }

    trackLeaveSuccess();
    reorderMachineRef.send({ type: 'CONFIRM_GROUP_ORDER_LEAVE' });

    // Forget the order so a later host submission or cancellation does not
    // surface a terminal-state message for an order this participant left.
    if (groupOrderId) {
      storeDismissedGroupOrderId(groupOrderId);
    }

    return true;
  }, [
    addNoticeBanner,
    executeLeaveGroupOrder,
    formatMessage,
    groupOrderId,
    reorderMachineRef,
    trackLeaveSuccess,
  ]);

  const continueToMenu = useCallback(
    (menuDestination: UseNavigateToMenuProps) => {
      sendToLastInteractedStoreMachine({ type: 'RESET_ACTIVE_CART_STORE' });
      setLastInteractedMenuStore(getLastInteractedMenuStore({ menuDestination }));
      navigateToMenu({ ...menuDestination, category });
    },
    [category, navigateToMenu, sendToLastInteractedStoreMachine, setLastInteractedMenuStore],
  );

  const leaveGroupOrderAndContinue = useCallback(
    async (reorderEvent: ReorderEvent) => {
      const menuDestination = getMenuDestination({ reorderEvent });

      if (!menuDestination) {
        return;
      }

      const hasLeftGroupOrder = await leaveGroupOrder();

      if (!hasLeftGroupOrder) {
        return;
      }

      continueToMenu(menuDestination);
    },
    [continueToMenu, leaveGroupOrder],
  );

  // ───────────────────────────────────────────────────────────────────────────

  return { isLeavingGroupOrder, leaveGroupOrderAndContinue };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getMenuDestination(params: GetMenuDestinationParams) {
  const { reorderEvent } = params;

  const {
    orderType,
    restaurantId,
    restaurantName,
    restaurantSlug,
    deliveryAddressName,
    deliveryOrderDetail,
  } = reorderEvent;

  if (!orderType) {
    return;
  }

  return {
    orderType,
    restaurantId,
    restaurantName,
    restaurantSlug,
    deliveryAddressName,
    deliveryOrderDetail,
  };
}

function getLastInteractedMenuStore(params: GetLastInteractedMenuStoreParams) {
  const { menuDestination } = params;

  const { restaurantId, restaurantName, restaurantSlug, deliveryOrderDetail } = menuDestination;

  return {
    restaurantId,
    restaurantName,
    restaurantSlug,
    addressId: deliveryOrderDetail?.addressId,
    deliveryFee: deliveryOrderDetail?.deliveryFee,
    vendor: deliveryOrderDetail?.vendor,
    vendorRestaurantId: deliveryOrderDetail?.vendorRestaurantId,
  };
}

function checkIfLeftGroupOrder(params: CheckIfLeftGroupOrderParams) {
  const { result } = params;

  return result?.__typename === 'LeaveOrderSuccess' && result.success;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  leaveError: {
    defaultMessage: 'We couldn’t leave the group order. Please try again.',
    description: 'Reorder | Group order leave modal | Leave error',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  isInsideModal?: boolean;
};

type GetMenuDestinationParams = {
  reorderEvent: ReorderEvent;
};

type GetLastInteractedMenuStoreParams = {
  menuDestination: UseNavigateToMenuProps;
};

type CheckIfLeftGroupOrderParams = {
  result: ReorderLeaveGroupOrderMutation['leaveMultipartyOrder'] | undefined;
};
